<template>
  <v-container fluid v-if="vehicle">
    <customer-context-prompt :resource="vehicle"/>
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('vehicle') }} {{vehicle.name}}</h2>
      <v-btn
        v-if="!vehicle.deleted && user.can('delete_vehicle')"
        outlined color="danger"
        @click="deleteVehicle()">
        <v-icon>mdi-delete</v-icon>
        {{ $t('delete_vehicle') }}
      </v-btn>
    </div>
    <dashboard-exclusion-dialog
      ref="dashboard-exclusion-dialog"
      :vehicle="vehicle"
      @update="updateDashboardExclusion($event)"/>
    <v-row>
      <v-col xl="6" lg="6" md="12">
        <v-card>
          <v-card-title>{{ $t('general')}}</v-card-title>
          <v-list dense class="mt-n5">
            <v-list-item>
              <v-row>
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('created') }}:
                </v-col>
                <v-col>
                  <multi-time-display :time="vehicle.created"/>
                </v-col>
              </v-row>
            </v-list-item>
            <v-list-item v-if="vehicle.deleted">
              <v-row>
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('deleted') }}:
                </v-col>
                <v-col>
                  <multi-time-display :time="vehicle.deleted"/>
                </v-col>
              </v-row>
            </v-list-item>
            <v-list-item v-if="userLoaded && user.can('assign_tenant_vehicle')">
              <v-row>
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('vehicle_ref_id') }}:
                </v-col>
                <v-col>
                  <if-set :value="vehicle.ref_id" />
                </v-col>
              </v-row>
            </v-list-item>
            <v-list-item>
              <v-row>
                <v-col lg="5" sm="auto" class="text-md-right text-sm-left align-self-center">
                  {{ $t('excluded_from_route_detection') }}:
                </v-col>
                <v-col>
                  <v-switch
                    dense
                    v-if="user.can('view_rootentity')"
                    :loading="updatingRouteDetectionExclusion"
                    v-model="vehicle.exclude_from_route_detection"
                    @click="updateRouteDetectionExclusion($event)"
                    :label="vehicle.exclude_from_route_detection ? $t('yes') : $t('no')">
                  </v-switch>
                  <span v-else>
                    {{ vehicle.exclude_from_route_detection ? $t('yes') : $t('no') }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>
            <v-list-item>
              <v-row>
                <v-col lg="5" sm="auto" class="text-md-right text-sm-left align-self-center">
                  {{ $t('excluded_from_customer_management') }}:
                </v-col>
                <v-col>
                  <v-switch
                    dense
                    v-if="user.can('view_rootentity')"
                    :loading="updatingCustomerManagement"
                    :input-value="isExcludedFromCustomerManagement"
                    @change="toggleExcludedFromCustomerManagement"
                    :label="isExcludedFromCustomerManagement ? $t('yes') : $t('no')">
                  </v-switch>
                  <span v-else>
                    {{ isExcludedFromCustomerManagement ? $t('yes') : $t('no') }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>
            <template v-if="isExcludedFromCustomerManagement && user.can('view_rootentity')">
              <v-list-item class="ml-8">
                <span class="text--secondary caption">
                  {{ $t('customer_management_info') }}
                </span>
              </v-list-item>
              <v-list-item class="ml-8">
                <v-row>
                  <v-col lg="5" sm="auto" class="text-md-right text-sm-left align-self-center">
                    {{ $t('exclude_from_auto_attach') }}:
                  </v-col>
                  <v-col>
                    <v-switch
                      dense
                      :loading="updatingCustomerManagement"
                      v-model="vehicle.exclude_from_customer_attach"
                      @change="updateCustomerManagement"
                      :label="vehicle.exclude_from_customer_attach ? $t('yes') : $t('no')">
                    </v-switch>
                  </v-col>
                </v-row>
              </v-list-item>
              <v-list-item class="ml-8">
                <v-row>
                  <v-col lg="5" sm="auto" class="text-md-right text-sm-left align-self-center">
                    {{ $t('exclude_from_auto_detach') }}:
                  </v-col>
                  <v-col>
                    <v-switch
                      dense
                      :loading="updatingCustomerManagement"
                      v-model="vehicle.exclude_from_customer_detach"
                      @change="updateCustomerManagement"
                      :label="vehicle.exclude_from_customer_detach ? $t('yes') : $t('no')">
                    </v-switch>
                  </v-col>
                </v-row>
              </v-list-item>
              <v-list-item class="ml-8">
                <v-row>
                  <v-col lg="5" sm="auto" class="text-md-right text-sm-left align-self-center">
                    {{ $t('exclude_from_parent_auto_attach') }}:
                  </v-col>
                  <v-col>
                    <v-switch
                      dense
                      :loading="updatingCustomerManagement"
                      v-model="vehicle.exclude_from_parent_customer_attach"
                      @change="updateCustomerManagement"
                      :label="vehicle.exclude_from_parent_customer_attach ? $t('yes') : $t('no')">
                    </v-switch>
                  </v-col>
                </v-row>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
        <v-card class="mt-4">
          <v-card-title>
            {{ $t('30_day_health_overview') }}
          </v-card-title>
          <v-list dense class="mt-n4">
            <v-list-item v-if="vehicle.service_provider">
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('telemetry_provider') }}:
                </v-col>
                <v-col>
                  {{ vehicle.service_provider.name }}
                  <span v-if="vehicle.state_updated" class="text-no-wrap">
                    (<multi-time-display :time="vehicle.state_updated" />)
                  </span>
                </v-col>
              </v-row>
              <v-list-item-action/>
            </v-list-item>
            <v-list-item>
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  GPS:
                </v-col>
                <v-col>
                  <if-set :value="vehicle.hasGpsCoordinates()">
                    <a
                      :href="`https://maps.google.com/maps?q=${vehicle.getGpsCoordinatesAsString()}`"
                      :class="vehicle.hasZeroedGpsCoordinates() ? 'zeroed-gps' : ''">
                      <v-icon>mdi-globe</v-icon>
                      {{vehicle.getGpsCoordinatesAsString()}}
                    </a>
                    <if-set :value="vehicle.getMostRecentGpsReading()">
                      <span class="text-no-wrap">(<multi-time-display :time="vehicle.getMostRecentGpsReading()"/>)</span>
                    </if-set>
                  </if-set>
                </v-col>
              </v-row>
              <v-list-item-action>
                <vehicle-view-issue-icon :status="gpsIssue.status" :tooltip="gpsIssue.tooltip"/>
              </v-list-item-action>
            </v-list-item>
            <v-list-item>
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('freezer') }}:
                </v-col>
                <v-col>
                  <if-set :value="vehicle.currentZoneTemp('freezer')">
                    <template #default="scope">
                      {{temperatureToCustomerScale(scope.value)}} &deg;{{ customerContext.tempFormat() }}
                      <span class="text-no-wrap">(<multi-time-display :time="vehicle.getMostRecentZoneReading('freezer')" />)</span>
                    </template>
                  </if-set>
                </v-col>
              </v-row>
              <v-list-item-action>
                <vehicle-view-issue-icon :status="freezerIssue.status" :tooltip="$t('vehicle_view_ui.no_freezer_data_warning')"/>
              </v-list-item-action>
            </v-list-item>
            <v-list-item>
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('cooler') }}:
                </v-col>
                <v-col>
                  <if-set :value="vehicle.currentZoneTemp('cooler')">
                    <template #default="scope">
                      {{temperatureToCustomerScale(scope.value)}} &deg;{{ customerContext.tempFormat() }}
                      <span class="text-no-wrap">(<multi-time-display :time="vehicle.getMostRecentZoneReading('cooler')" />)</span>
                    </template>
                  </if-set>
                </v-col>
              </v-row>
              <v-list-item-action>
                <vehicle-view-issue-icon :status="coolerIssue.status" :tooltip="$t('vehicle_view_ui.no_cooler_data_warning')"/>
              </v-list-item-action>
            </v-list-item>
            <v-list-item>
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('cooler_temperature_health_warning') }}:
                </v-col>
                <v-col>
                  <div v-if="isEmpty(coolerNominalIssue.impacted_route_ids)">
                    {{ $t('n_of_m_routes', { n: 0, m: coolerRoutesInEvaluationPeriod }) }}
                  </div>
                  <a v-else :href="linkToRouteIds(coolerNominalIssue.impacted_route_ids)">
                    {{ $t('n_of_m_routes', {'n': size(coolerNominalIssue.impacted_route_ids), m: coolerRoutesInEvaluationPeriod }) }}
                  </a>
                  <div v-if="isWithin30Days(vehicle.marked_as_fixed)" class="text-body-2">
                    {{$t('since')}}
                    <multi-time-display :time="vehicle.marked_as_fixed" />
                    <a href="#" @click="showNotes"> ({{$t('see_notes')}})</a>
                  </div>
                </v-col>
              </v-row>
              <v-list-item-action>
                <component :is="size(coolerNominalIssue.impacted_route_ids) > 0 ? 'a' : 'div'" :href="linkToRouteIds( get(coolerNominalIssue, 'impacted_route_ids', []) )">
                  <vehicle-view-issue-icon
                    :status="coolerNominalIssue.status"
                    :tooltip="$t('vehicle_view_ui.temperature_health_warning_tip', { temp: temperatureToCustomerScale(nominalCoolerTemperature), tempFormat: customerContext.tempFormat(), zone: $t('cooler') } )"/>
                </component>
              </v-list-item-action>
            </v-list-item>
            <v-list-item>
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('freezer_temperature_health_warning') }}:
                </v-col>
                <v-col>
                  <div v-if="isEmpty(freezerNominalIssue.impacted_route_ids)">
                    {{ $t('n_of_m_routes', { n: 0, m: coolerRoutesInEvaluationPeriod }) }}
                  </div>
                  <a v-else :href="linkToRouteIds(freezerNominalIssue.impacted_route_ids)">
                    {{ $t('n_of_m_routes', {'n': size(freezerNominalIssue.impacted_route_ids), m: freezerRoutesInEvaluationPeriod }) }}
                  </a>
                  <div v-if="isWithin30Days(vehicle.marked_as_fixed)" class="text-body-2">
                    {{$t('since')}}
                    <multi-time-display :time="vehicle.marked_as_fixed" />
                    <a href="#" @click="showNotes"> ({{$t('see_notes')}})</a>
                  </div>
                </v-col>
              </v-row>
              <v-list-item-action>
                <component :is="size(freezerNominalIssue.impacted_route_ids) > 0 ? 'a' : 'div'" :href="linkToRouteIds( get(freezerNominalIssue, 'impacted_route_ids', []) )">
                  <vehicle-view-issue-icon
                    :status="freezerNominalIssue.status"
                    :tooltip="$t('vehicle_view_ui.temperature_health_warning_tip', { temp: temperatureToCustomerScale(nominalFreezerTemperature), tempFormat: customerContext.tempFormat(), zone: $t('freezer') } )"/>
                </component>
              </v-list-item-action>
            </v-list-item>
            <v-list-item>
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('sensors_swapped') }}:
                </v-col>
                <v-col>
                  <span v-if="sensorsSwappedIssue.status === 'unhealthy'">
                    {{ $t('yes') }}
                  </span>
                  <span v-else>
                    {{ $t('no') }}
                  </span>
                </v-col>
              </v-row>
              <v-list-item-action>
                <vehicle-view-issue-icon :status="sensorsSwappedIssue.status" :tooltip="$t('vehicle_view_ui.sensors_swapped')"/>
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="customerContext.getSetting('enableSetPointViolation')">
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('set_point_not_reached') }}:
                </v-col>
                <v-col>
                  {{ $t('n_of_m_routes', {'n': setPointViolationIssue.violating_routes, m: setPointViolationIssue.total_routes }) }}
                </v-col>
              </v-row>
              <v-list-item-action>
                <vehicle-view-issue-icon :status="setPointViolationIssue.status" :tooltip="$t('vehicle_view_ui.')"/>
              </v-list-item-action>
            </v-list-item>
            <v-list-item>
              <v-row class="align-center">
                <v-col lg="5" sm="auto" class="text-md-right text-left">
                  {{ $t('dashboard_status') }}:
                </v-col>
                <v-col>
                  <a href="#" @click="$refs['dashboard-exclusion-dialog'].showDialog()">
                    {{ vehicle.exclude_from_dashboard ? $t('excluded') : $t('included') }}
                  </a>
                </v-col>
              </v-row>
              <v-list-item-action>
                <vehicle-view-issue-icon :status="dashboardExclusionStatus.status" :tooltip="$t('vehicle_view_ui.vehicle_excluded_from_dashboard')"/>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col xl="6" lg="6" md="12">
        <v-card>
          <v-card-title>
            {{ $t('recent_routes' )}}
          </v-card-title>
          <v-card-text>
            <vehicle-recent-routes :vehicle="vehicle"/>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-divider class="mt-6 mb-4"/>
    <v-card outlined rounded class="mt-6">
      <v-card-text>
        <v-tabs color="primary-700" show-arrows>
          <v-tabs-slider color="primary-700"/>
          <!-- Metadata -->
          <v-tab>
            {{ $t('additional_data') }}
          </v-tab>
          <v-tab-item class="vf-border-top tab-content" :transition="false">
            <vehicle-metadata :vehicle="vehicle" ref="vehicle-metadata"/>
          </v-tab-item>
          <!-- Vehicle Sensors -->
          <v-tab>
            {{ $tc('vehicle_sensor', 2) }}
          </v-tab>
          <v-tab-item class="vf-border-top tab-content pt-5" :transition="false">
            <vehicle-sensor-history ref="sensor-history" :vehicle="vehicle"/>
          </v-tab-item>
          <!-- Vehicle Positions -->
          <v-tab>
            {{ $tc('vehicle_position', 2) }}
          </v-tab>
          <v-tab-item class="vf-border-top tab-content pt-5" :transition="false">
            <vehicle-gps-map ref="vehicle-gps-map" :vehicle="vehicle"/>
          </v-tab-item>
          <!-- Notes -->
          <v-tab>
            {{ $tc('note', 2) }}
            <v-badge v-if="notesCount" :content="notesCount" inline style="margin: 0 0 3px 4px"/>
          </v-tab>
          <v-tab-item class="vf-border-top tab-content pt-4" :transition="false">
            <resource-notes
              ref="vehicleNotes"
              v-model="newNoteContent"
              :notes="vehicle.notes"
              v-on:create-note="createNote($event)"
              :limitToContexts="['frontend/form/vehicle', 'frontend/form/vehicle/mark-as-fixed']"
              :iconMap="{'frontend/form/vehicle/mark-as-fixed': 'tools'}" />
          </v-tab-item>
          <!-- Audit log -->
          <v-tab>
            {{ $tc('audit_log') }}
          </v-tab>
          <v-tab-item class="vf-border-top tab-content pt-4" :transition="false">
            <audit-log
              ref="audit-log"
              autoload
              :table-controls="['actor', 'date_range']"
              :table-columns="['user', 'customer', 'action', 'timestamp']"
              :initSearchCriteria="auditLogSearchCriteria"/>
          </v-tab-item>
          <v-tab v-if="user.can('assign_tenant_vehicle')">
            {{ $tc('vehicle_customer', 2) }}
          </v-tab>
          <v-tab-item v-if="user.can('assign_tenant_vehicle')" class="vf-border-top pt-4" :transition="false">
            <vehicle-customer-management :vehicle="vehicle"/>
          </v-tab-item>
          <v-tab v-if="user.can('view_rootentity')">
            Sensor History
          </v-tab>
          <v-tab-item v-if="user.can('view_rootentity')" class="vf-border-top tab-content pt-5" :transition="false">
            <vehicle-sensor-data :vehicle="vehicle"/>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import DashboardExclusionDialog from '@/components/vehicle/DashboardExclusionDialog'
import VehicleSensorHistory from '@/components/vehicle/VehicleSensorHistory'
import VehicleRecentRoutes from '@/components/vehicle/VehicleRecentRoutes'
import ResourceNotes from '@/components/shared/ResourceNotes.vue'
import VehicleCustomerManagement from '@/components/vehicle/VehicleCustomerManagement'
import CustomerContextPrompt from '@/components/shared/CustomerContextPrompt'
import IfSet from '@/components/shared/IfSet'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import VehicleViewIssueIcon from '@/components/vehicle/VehicleViewIssueIcon'
import VehicleGpsMap from '@/components/vehicle/VehicleGpsMap'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import VehicleIssueRecord from '@/orm/models/VehicleIssueRecord'
import AuditLog from '@/components/audit-log/AuditLog.vue'
import VehicleMetadata from '@/components/vehicle/VehicleMetadata'
import VuelidateInvalidFeedback from '@/components/shared/VuelidateInvalidFeedback'
import VehicleSensorData from '@/components/vehicle/VehicleSensorData'
import Moment from 'moment-timezone'
import { and as vuelidateAnd, minLength } from 'vuelidate/lib/validators'
import { find, get, cloneDeep, remove, indexOf, size, isEmpty } from 'lodash'

export default {
  name: 'VehicleView',
  components: {
    DashboardExclusionDialog,
    VehicleSensorHistory,
    VehicleRecentRoutes,
    ResourceNotes,
    VehicleCustomerManagement,
    CustomerContextPrompt,
    IfSet,
    MultiTimeDisplay,
    VehicleViewIssueIcon,
    AuditLog,
    VehicleGpsMap,
    VehicleMetadata,
    VuelidateInvalidFeedback,
    VehicleSensorData
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin,
    TemperatureMixin
  ],
  validations: {
    excludeFromInsightsReason: {
      requiredMinLength: vuelidateAnd(
        minLength(3),
        function (value) {
          if (this.updatingDashboardExclusion && value === '') {
            return false
          }
          return true
        })
    }
  },
  data () {
    return {
      vehicle: null,
      newNoteContent: '',
      updatingAssignedCustomers: false,
      routesLoading: true,
      // Set to true when the map card is opened. This prevents us from
      // initializing the map unless requested to.
      vehicleGpsMapShown: false,
      metadataShown: false,
      // Whether we're currently updating the Insights Dashboard exclusion.
      updatingDashboardExclusion: false,
      // Whether we're currently updating the Route detectoin exclusion.
      updatingRouteDetectionExclusion: false,
      updatingCustomerManagement: false,
      excludeFromInsightsReason: '',
      nominalCoolerTemperature: RouteViolationRecord.DEFAULT_NOMINAL_COOLER_TEMPERATURE,
      nominalFreezerTemperature: RouteViolationRecord.DEFAULT_NOMINAL_FREEZER_TEMPERATURE,
      notesVisible: false,
      scrollToNotesOnOpen: false
    }
  },
  computed: {
    auditLogSearchCriteria () {
      return {
        resourceType: 'vehicle',
        createdAfter: this.vehicle.created,
        primaryKey: this.vehicle.id
      }
    },
    /**
     * Computed property to determine whether there is currently a gps issue.
     * @return Object
     */
    gpsIssue () {
      // Check for No GPS first
      if (this.hasHealthIssue('no-gps-data')) {
        return {
          status: 'unhealthy',
          tooltip: this.$t('vehicle_view_ui.no_gps_warning')
        }
      } else if (this.hasHealthIssue('gps-zero')) {
        return {
          status: 'unhealthy',
          tooltip: this.$t('vehicle_view_ui.gps_zero_warning')
        }
      } else {
        return { status: 'healthy' }
      }
    },
    /**
     * convenience accessor for Swapped Sensors issue
     * @return Object
     */
    sensorsSwappedIssue () {
      return this.getHealthIssue(RouteViolationRecord.TYPE_SWAPPED_AIR_SENSORS) || {}
    },
    /**
     * convenience property for No Cooler Data issue
     * @return Object
     */
    coolerIssue () {
      return this.getHealthIssue(VehicleIssueRecord.TYPE_NO_COOLER_SENSOR_DATA) || {}
    },
    /**
     * convenience property for No Freezer Data issue
     * @return Object
     */
    freezerIssue () {
      return this.getHealthIssue(VehicleIssueRecord.TYPE_NO_FREEZER_SENSOR_DATA) || {}
    },
    /**
     * convenience property for Excessive Hot Violation issue
     * @return Object
     */
    hotViolationIssue () {
      return this.getHealthIssue(VehicleIssueRecord.TYPE_CONSISTENT_HOT_VIOLATIONS) || {}
    },
    /**
     * convenience property for Excessive Cold Violation issue
     * @return Object
     */
    coldViolationIssue () {
      return this.getHealthIssue(VehicleIssueRecord.TYPE_CONSISTENT_COLD_VIOLATIONS) || {}
    },
    /**
     * convenience property for Excessive Set Point Violation issue
     * @return Object
     */
    setPointViolationIssue () {
      return this.getHealthIssue(VehicleIssueRecord.TYPE_CONSISTENT_SET_POINT_VIOLATIONS) || {}
    },
    /**
     * @return int
     */
    totalRoutesInEvaluationPeriod () {
      return get(this.vehicle, 'vehicle_health.routeTotals.allZones', 0)
    },
    /**
     * @return int
     */
    coolerRoutesInEvaluationPeriod () {
      return get(this.vehicle, 'vehicle_health.routeTotals.cooler', 0)
    },
    /**
     * @return int
     */
    freezerRoutesInEvaluationPeriod () {
      return get(this.vehicle, 'vehicle_health.routeTotals.freezer', 0)
    },
    /**
     * Convenience property for nominal-air-temp-not-reached issue associated with the cooler
     * @return Object
     */
    coolerNominalIssue () {
      return this.getHealthIssue('cooler-' + RouteViolationRecord.TYPE_NOMINAL_AIR_TEMP_NOT_REACHED) || {}
    },
    /**
     * Convenience property for nominal-air-temp-not-reached issue associated with the freezer
     * @return Object
     */
    freezerNominalIssue () {
      return this.getHealthIssue('freezer-' + RouteViolationRecord.TYPE_NOMINAL_AIR_TEMP_NOT_REACHED) || {}
    },
    /** Computed property to give the current state of the exclusion status, to help with showing a spinner on the status icon. */
    dashboardExclusionStatus () {
      if (this.updatingDashboardExclusion) {
        return {
          status: 'updating'
        }
      }
      if (this.vehicle.exclude_from_dashboard) {
        return {
          status: 'unhealthy'
        }
      }
      return {
        status: 'healthy'
      }
    },
    /**
     * Get the number of applicable notes for the vehicle (displayed in a badge on the notes tab)
     * @returns {number}
     */
    notesCount () {
      return size(
        this.filterNotes(this.vehicle.notes, [
          'frontend/form/vehicle',
          'frontend/form/vehicle/mark-as-fixed'
        ])
      )
    },
    isExcludedFromCustomerManagement () {
      return this.vehicle.exclude_from_customer_attach ||
        this.vehicle.exclude_from_customer_detach ||
        this.vehicle.exclude_from_parent_customer_attach
    }
  },
  methods: {
    get: get,
    size: size,
    isEmpty: isEmpty,
    /**
     * Small handler function to update the vehicle model when the DashboardExclusionDialog updates the vehicle's
     * exclude_from_dashboard flag.
     * @param value
     */
    updateDashboardExclusion (value) {
      this.vehicle.exclude_from_dashboard = value
    },
    /**
     * Update the value of vehicle.exclude_from_route_detection in the API
     */
    async updateRouteDetectionExclusion (value) {
      try {
        this.updatingRouteDetectionExclusion = true
        await this.$orm('Vehicle').update(this.vehicle.id, {
          exclude_from_route_detection: this.vehicle.exclude_from_route_detection
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.updatingRouteDetectionExclusion = false
      }
    },
    async toggleExcludedFromCustomerManagement (value) {
      try {
        this.updatingCustomerManagement = true
        const newValue = !!value
        this.vehicle.exclude_from_customer_attach = newValue
        this.vehicle.exclude_from_customer_detach = newValue
        this.vehicle.exclude_from_parent_customer_attach = newValue
        await this.$orm('Vehicle').update(this.vehicle.id, {
          exclude_from_customer_attach: newValue,
          exclude_from_customer_detach: newValue,
          exclude_from_parent_customer_attach: newValue
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.updatingCustomerManagement = false
      }
    },
    async updateCustomerManagement () {
      try {
        this.updatingCustomerManagement = true
        await this.$orm('Vehicle').update(this.vehicle.id, {
          exclude_from_customer_attach: this.vehicle.exclude_from_customer_attach,
          exclude_from_customer_detach: this.vehicle.exclude_from_customer_detach,
          exclude_from_parent_customer_attach: this.vehicle.exclude_from_parent_customer_attach
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.updatingCustomerManagement = false
      }
    },
    async deleteVehicle () {
      this.$gtag.event('delete', { event_category: 'Vehicle' })
      try {
        await this.$orm('Vehicle').destroy(this.vehicle.id)
        this.$router.push('/vehicles', () => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$tc('vehicle')} ${this.vehicle.name} ${this.$t('generic_delete_success')}`
          )
        })
      } catch (error) {
        this.handleError(error)
      }
    },
    async createNote (note) {
      const newNote = {
        resource: 'vehicle',
        entity_id: this.vehicle.id,
        user_id: this.user.id,
        note: note,
        context: 'frontend/form/vehicle'
      }
      try {
        const note = await this.$orm('Note').createOrUpdate(newNote, {
          params: {
            expand: 'user_email',
            fields: 'id,note,created,user_email'
          }
        })
        this.vehicle.notes.push(note)
        this.$refs.vehicleNotes.resetNoteBody()
        await this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$tc('note')} ${this.$t('generic_save_success')}`
        )
      } catch (error) {
        this.handleError(error)
      } finally {
        this.$refs.vehicleNotes.newNoteSaveComplete()
      }
    },
    /**
     * Notify the sensor history component that it has been shown. This is
     * particularly important because of how the cart (embedded in the component
     * is rendered)
     */
    sensorHistoryShown () {
      if (this.$refs['sensor-history']) {
        this.$refs['sensor-history'].shown()
      }
    },
    /**
     * Return if the vehicle has a health issue associated with the issue type
     * @param issueType
     * @returns {*}
     */
    hasHealthIssue (issueType) {
      const healthIssue = this.getHealthIssue(issueType)
      return healthIssue != null && healthIssue.status === 'unhealthy'
    },
    /**
     * Return the first found health issue of the specified issue type.
     * @param issueType
     * @returns {*}
     */
    getHealthIssue (issueType) {
      const issues = get(this.vehicle, 'vehicle_health.issues', [])
      return find(issues, issue => {
        return issue.type === issueType
      })
    },
    /**
     * Returns the URL for the route index of the routes with the specified violation.
     * @param violationType (e.g. hot, cold)
     * @return string
     */
    violatingRoutesLink (violationType) {
      const startDate = Moment().tz(this.customerContext.timezone()).subtract(30, 'day').format('YYYY-MM-DD HH:mm:ss')
      const endDate = Moment().tz(this.customerContext.timezone()).format('YYYY-MM-DD HH:mm:ss')

      return `/route/index?startDate=${startDate}&endDate=${endDate}&violations[]=${violationType}&vehicleId=${this.vehicle.id}`
    },
    /**
     * Returns the URL for the route index of the routes with the specified ids.
     * @param array routeIds
     * @return string
     */
    linkToRouteIds (routeIds) {
      return `/route/index?route_id=${routeIds.join(',')}`
    },
    /**
     * Filter out the notes that don't match the given contexts. this duplicates logic in the ResourceNotes component,
     * but when I try to read the computed method off the child component, it seems to throw javascript into a tail spin
     * (I suspect there's some sort of recursive loop issue.)
     *
     * @var Array<NoteRecord> notes
     * @var Array an array of contexts to keep in the set. If the note has at
     * least one of the contexts defined then include it in the returned records
     * @return Array
     */
    filterNotes (notes, contexts) {
      const filteredNotes = cloneDeep(notes)
      if (contexts.length > 0) {
        remove(filteredNotes, (note) => {
          return indexOf(contexts, note.context) === -1
        })
      }
      return filteredNotes
    },
    /**
     * Updates the state that controls the visibility of the notes accordion and updates an internal flag that will
     * cause the page to scroll to the notes once fully rendered.
     * @return undefined
     */
    showNotes () {
      this.scrollToNotesOnOpen = true
      this.notesVisible = true
    },
    /**
     * Scrolls the window to the notes section if this.scrollToNotesOnOpen is true.
     * @return undefined
     */
    scrollToNotes () {
      if (this.scrollToNotesOnOpen) {
        this.$refs.vehicleNotes.$el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    /**
     * Return if the given time is less than 30 days old
     * @param timeString
     * @returns {boolean}
     */
    isWithin30Days (timeString) {
      if (timeString === null) {
        return false
      }
      return Moment(timeString).add(30, 'days').isSameOrAfter(Moment())
    }
  },
  mounted () {
    const expand = [
      'service_provider',
      'customers',
      'primary_customer',
      'billing_customer',
      'notes.user_email',
      'vehicle_health'
    ]
    const fields = [
      'id',
      'name',
      'created',
      'deleted',
      'ref_id',
      'state',
      'state_updated',
      'service_provider',
      'exclude_from_dashboard',
      'exclude_from_route_detection',
      'exclude_from_customer_attach',
      'exclude_from_customer_detach',
      'exclude_from_parent_customer_attach',
      'marked_as_fixed',
      'customers.id',
      'customers.name',
      'primary_customer.id',
      'primary_customer.name',
      'billing_customer.id',
      'billing_customer.name',
      'notes.id',
      'notes.note',
      'notes.created',
      'notes.user_email',
      'notes.context',
      'meta'
    ]
    this.$orm('Vehicle')
      .find(this.$route.params.id, {
        params: {
          expand: expand.join(','),
          fields: fields.join(',')
        }
      })
      .then((vehicle) => {
        this.vehicle = vehicle
        this.updateBrowserTitle(`${this.$tc('vehicle')} ${vehicle.name}`)
      })
      .catch(error => {
        this.handleError(error)
      })
  }
}
</script>

<style scoped lang="scss">
.tab-content {
  min-height: 350px;
}
</style>
